/**
 * Date formatting helpers — Australian timezone (Sydney) day-of-week.
 *
 * Use these everywhere a specific calendar day is rendered so the user
 * always sees the weekday alongside the date (e.g. "Mon 2026-05-12").
 */

const AU_TZ = "Australia/Sydney";
const SHORT_DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Parse a YYYY-MM-DD (or full ISO) string into a Date, treating
 *  a bare date as local midnight Sydney time. Returns null when invalid. */
function _toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input) ? null : input;
  const s = String(input);
  // Bare YYYY-MM-DD → interpret as Sydney midnight
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    // Use UTC then offset doesn't matter for getDay because we use formatToParts below
    return new Date(`${s}T00:00:00+10:00`);
  }
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/** Returns "Mon" / "Tue" / … for the supplied date in Australian time, or "" when invalid. */
export function dowAu(input) {
  const d = _toDate(input);
  if (!d) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-AU", {
      timeZone: AU_TZ, weekday: "short",
    }).formatToParts(d);
    const wd = parts.find((p) => p.type === "weekday")?.value;
    return wd || SHORT_DOW[d.getDay()];
  } catch {
    return SHORT_DOW[d.getDay()] || "";
  }
}

/** Returns "Mon 2026-05-12" for the supplied date (Sydney time). */
export function formatAuDate(input) {
  const d = _toDate(input);
  if (!d) return String(input || "");
  const dow = dowAu(d);
  // Render as YYYY-MM-DD in Sydney TZ for consistency
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: AU_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
  return dow ? `${dow} ${ymd}` : ymd;
}

/** "2026-01" → "January 2026" (en-AU). Returns input unchanged on parse failure. */
export function formatPeriodLong(period) {
  if (!period || !/^\d{4}-\d{2}$/.test(period)) return String(period || "");
  const [y, m] = period.split("-").map((s) => parseInt(s, 10));
  if (!y || !m) return period;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "UTC", year: "numeric", month: "long",
  }).format(d);
}

/** Returns "Mon 2026-05-12 · 14:32" — Sydney TZ, 24-hour clock. */
export function formatAuDateTime(input) {
  const d = _toDate(input);
  if (!d) return String(input || "");
  const dow = dowAu(d);
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: AU_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
  const hm = new Intl.DateTimeFormat("en-AU", {
    timeZone: AU_TZ, hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(d);
  return `${dow} ${ymd} · ${hm}`;
}
